import classNames from 'classnames';
import { NumberFormatter } from 'components/shared/NumberFormatter/NumberFormatter';
import { Switch } from 'components/shared/Switch/Switch';
import type { DealSplitIndependentAmount, DealSplitIndependentAmountType } from 'models/forms/DealSplitData';
import { FC } from 'react';

import './IndependentAmountField.scss';

export interface IndependentAmountFieldProps {
  independentAmount: DealSplitIndependentAmount;
  currency: string;
  isDisabled?: boolean;
  index: number;
  isChecked: boolean;
  isCollapsed: boolean;
  onToggleSwitch: (isChecked: boolean) => void;
  onIndependentAmountChanged: (independentAmount: DealSplitIndependentAmount) => void;
}

const resetAmountField = (
  type: DealSplitIndependentAmountType,
  independentAmount: DealSplitIndependentAmount,
): DealSplitIndependentAmount => ({
  ...independentAmount,
  Type: type,
  Value: undefined,
});

export const IndependentAmountField: FC<IndependentAmountFieldProps> = ({
  independentAmount,
  currency,
  isDisabled,
  index,
  isChecked,
  isCollapsed,
  onToggleSwitch,
  onIndependentAmountChanged,
}) => {
  const currentType = independentAmount.Type ?? 'Percentage';
  const hasError = independentAmount.Value === null;
  const shouldShowIndependentAmountFields = isChecked && !isCollapsed;

  return (
    <>
      <div className="px-1 cursor-pointer">
        <Switch
          id={`dealSplit-ia-toggle-${index}`}
          value={isChecked}
          isDisabled={isDisabled}
          e2e={`dealSplit-ia-toggle-${index}`}
          onToggleSwitch={onToggleSwitch}
        />
      </div>

      <div
        className={classNames('d-flex px-1 independent-amount-details', {
          'is-hidden': !shouldShowIndependentAmountFields,
        })}
        aria-hidden={!shouldShowIndependentAmountFields}
      >
        <div className="flex-grow-1 independent-amount-value">
          <NumberFormatter
            className="text-start"
            isDisabled={isDisabled || !shouldShowIndependentAmountFields}
            e2e={`dealSplit-ia-value-${index}`}
            value={independentAmount.Value}
            hasError={hasError}
            onBlur={value =>
              onIndependentAmountChanged({
                ...independentAmount,
                Value: value ?? undefined,
              })
            }
            precision={4}
            decimalPadding={false}
          />
        </div>
        <div className="flex-shrink-0 independent-amount-unit">
          <select
            className="form-select form-control-sm"
            id={`dealSplit-ia-unit-${index}`}
            value={currentType}
            disabled={isDisabled || !shouldShowIndependentAmountFields}
            data-e2e={`dealSplit-ia-unit-${index}`}
            onChange={event =>
              onIndependentAmountChanged(
                resetAmountField(event.currentTarget.value as DealSplitIndependentAmountType, independentAmount),
              )
            }
          >
            <option value="Currency">{currency}</option>
            <option value="Percentage">%</option>
          </select>
        </div>
      </div>
    </>
  );
};
